import {
	CommonModule,
	CurrencyPipe,
	isPlatformBrowser,
	registerLocaleData,
} from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeNl from '@angular/common/locales/nl';
import type {
	AfterViewInit,
	ElementRef,
	OnDestroy,
	OnInit,
} from '@angular/core';
import {
	Component,
	DOCUMENT,
	inject,
	InjectionToken,
	input,
	model,
	NgZone,
	PLATFORM_ID,
	signal,
	ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import type { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { Observable } from 'rxjs';
import {
	BehaviorSubject,
	distinctUntilChanged,
	filter,
	interval,
	map,
	startWith,
	Subject,
	switchMap,
	takeUntil,
} from 'rxjs';

import { AlertService } from '@valk-nx/components/ui-alert/src/lib/services/alert.service';
import { RecaptchaNotificationComponent } from '@valk-nx/components/ui-recaptcha-notification/src/lib/recaptcha-notification';
import type { Language } from '@valk-nx/core/lib/core';
import { PricePipe } from '@valk-nx/core/lib/pipes/price/price.pipe';
import { SharedFormlyModule } from '@valk-nx/formly/lib/formly.module';
import { GeneralHelper } from '@valk-nx/helpers/lib/general/general.helper';
import { PostalCodeFacade } from '@valk-nx/postal-code-store/lib/postal-code.facade';
import type { GetAddressInterface } from '@valk-nx/postal-code-store/lib/postal-code.interface';
import { RouteHelper } from '@valk-nx/router-store/router.helper';
import { StoryblokDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok.directive';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { alertErrorSettings } from '@valk-nx/storyblok-services/src/lib/globals';
import type { GiftcardCustomer } from '@valk-nx/storyblok-services/src/lib/services/giftcard.service';
import {
	GiftcardService,
	POLLING_TIME,
} from '@valk-nx/storyblok-services/src/lib/services/giftcard.service';
import { TagManagerFacade } from '@valk-nx/storyblok-store/src/lib/store/tag-manager/tag-manager.facade';

import { OrderGiftcardProcessingComponent } from './processing/order-giftcard-processing';
import { OrderGiftcardSummaryComponent } from './summary/order-giftcard-summary';
import type { OrderSummary } from './order-giftcard.types';
import { OrderStatus, OrderSteps } from './order-giftcard.types';
import { form as formFields, orderFormGroup } from './order-giftcard-form';

registerLocaleData(localeNl, 'nl');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeEs, 'es');

export const SESSION_STORAGE = new InjectionToken<Storage>('session_storage');

@Component({
	selector: 'sb-order-giftcard',
	templateUrl: 'order-giftcard.html',
	host: { class: 'w-full', ngSkipHydration: 'true' },
	imports: [
		CommonModule,
		OrderGiftcardProcessingComponent,
		OrderGiftcardSummaryComponent,
		RecaptchaNotificationComponent,
		SharedFormlyModule,
		StoryblokDirective,
		TranslatePipe,
	],
	providers: [PricePipe],
})
export class OrderGiftcardComponent
	extends StoryblokRootDirective
	implements OnInit, AfterViewInit, OnDestroy
{
	private readonly translate = inject(TranslateService);
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly giftcardService = inject(GiftcardService);
	private readonly currencyPipe = inject(CurrencyPipe);
	private readonly sessionStorage = inject<Storage>(SESSION_STORAGE);
	readonly alertService = inject(AlertService);
	document = inject<Document>(DOCUMENT);
	readonly pollingTime = inject(POLLING_TIME);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly postalCodeFacade = inject(PostalCodeFacade);
	private readonly tagManagerFacade = inject(TagManagerFacade);
	private readonly ngZone = inject(NgZone);

	currency = input<string>('EUR');
	productCode = input<string>('');
	giftcardName = input<string>('Van der Valk Giftcard');
	maxAmountCards = input<number>(25);
	maxOrderCardAmount = input<number>(150);
	minOrderCardAmount = input<number>(5);
	isEGiftcard = input<boolean>(false);

	// Only an input so it can be used with storybook easily
	orderSummary = model<OrderSummary | undefined>(undefined);
	orderStep = model<OrderSteps>(OrderSteps.Form);

	orderStatus = signal<OrderStatus>(OrderStatus.success);
	isLoading = signal<boolean>(false);

	@ViewChild('orderGiftcardContainer') orderGiftcardContainer!: ElementRef;

	stopPolling$ = new Subject<void>();
	stopGUIDcheck$ = new Subject<void>();
	stopPostalCodeCheck$ = new Subject<void>();
	orderGUID = '';
	timeInterval$: Observable<number>;

	orderSteps = OrderSteps;

	options: FormlyFormOptions = {};
	formFields!: FormlyFieldConfig[];
	form = orderFormGroup;
	/* eslint-disable  @typescript-eslint/no-explicit-any */
	model: any = {};

	language: Language;

	orderDataSessionName: 'eOrderGiftcardFormData' | 'orderGiftcardFormData' =
		'orderGiftcardFormData';
	orderSummarySessionName: 'eOrderSummary' | 'orderSummary' = 'orderSummary';

	constructor() {
		super();
		const pollingTime = this.pollingTime;

		this.language = (this.translate.getCurrentLang() ??
			this.translate.getFallbackLang()) as Language;

		this.timeInterval$ = interval(+pollingTime || 5000);
	}

	ngOnInit(): void {
		this.orderDataSessionName = this.isEGiftcard()
			? 'eOrderGiftcardFormData'
			: 'orderGiftcardFormData';
		this.orderSummarySessionName = this.isEGiftcard()
			? 'eOrderSummary'
			: 'orderSummary';
		this.formFields = formFields({
			formName: this.giftcardName(),
			maxAmountCards: this.maxAmountCards(),
			minOrderValueCards: this.minOrderCardAmount(),
			maxOrderValueCards: this.maxOrderCardAmount(),
			minOrderValueCardsCurrency: this.currencyPipe.transform(
				this.minOrderCardAmount(),
				'EUR',
				'symbol-narrow',
				'1.2-2',
				this.language,
			),
			maxOrderValueCardsCurrency: this.currencyPipe.transform(
				this.maxOrderCardAmount(),
				'EUR',
				'symbol-narrow',
				'1.2-2',
				this.language,
			),
			isEGiftcard: this.isEGiftcard(),
		});

		this.postalCodeFacade
			.resolvePostalCode(
				this.form.get('customer')!.valueChanges.pipe(
					map((formValues) => {
						return {
							country: formValues.countryCode,
							houseNumber: formValues.houseNumber,
							houseNumberAddition: formValues.houseNumberAddition,
							postalCode: formValues.postalCode,
						};
					}),
					filter(
						(lookupValues) =>
							lookupValues.postalCode && lookupValues.houseNumber,
					),
					distinctUntilChanged(),
					takeUntil(this.stopPostalCodeCheck$),
				),
				new BehaviorSubject({} as GetAddressInterface),
				(value) => this.form.get('customer')?.patchValue(value as any),
			)
			.subscribe();

		this.orderGUID =
			this.activatedRoute.snapshot?.queryParams['orderguid'] ?? '';
		this.loadFormData();
	}

	ngAfterViewInit(): void {
		if (isPlatformBrowser(this.platformId) && this.orderGUID) {
			setTimeout(() => {
				this.toProcessingStep();
			}, 100);
		}
	}

	onSubmit(): void {
		/* v8 ignore start */
		if (isPlatformBrowser(this.platformId)) {
			/* v8 ignore stop */
			if (this.form.valid && !this.isLoading()) {
				this.isLoading.set(true);
				const numCards = Number.parseInt(
					this.form.value.amountCards,
					10,
				);
				const orderValue =
					Number.parseInt(this.form.value.orderValueCards, 10) * 100;
				this.giftcardService
					.getOrderSummary(
						numCards,
						orderValue,
						this.productCode(),
						this.form.value.customer!.countryCode,
						this.isEGiftcard(),
					)
					.then((order) => {
						this.orderSummary.set(order);
						this.toSummaryStep();
					})
					.finally(() => {
						this.isLoading.set(false);
					});
			} else {
				this.scrollToError();
			}
		}
	}

	onCreateOrder() {
		this.isLoading.set(true);
		/* v8 ignore start */
		if (isPlatformBrowser(this.platformId)) {
			/* v8 ignore stop */
			this.saveFormData();
			const numCards = Number.parseInt(this.form.value.amountCards, 10);
			const orderValue =
				Number.parseInt(this.form.value.orderValueCards, 10) * 100;
			this.giftcardService
				.createOrder(
					numCards,
					orderValue,
					this.productCode(),
					this.form.value.customer as GiftcardCustomer,
					this.form.value.differentDeliveryAddress
						? (this.form.value.recipient as GiftcardCustomer)
						: undefined,
					this.isEGiftcard(),
					this.form.value.message,
					document.location.href,
				)
				.then((orderResponse) => {
					this.isLoading.set(false);
					this.orderGUID = orderResponse.orderGUID;
					RouteHelper.redirectToExternalUrl(
						orderResponse.redirectURL,
					);
				})
				.catch(() => {
					this.isLoading.set(false);
				});
		}
	}

	toFormStep(): void {
		this.orderStep.set(this.orderSteps.Form);
		this.scrollToTopOfOrderGiftcardContainer();
	}

	toSummaryStep(): void {
		this.orderStep.set(this.orderSteps.Summary);
		this.scrollToTopOfOrderGiftcardContainer();
	}

	toProcessingStep(): void {
		this.orderStep.set(this.orderSteps.Processing);
		this.startOrderStatusPolling();

		this.scrollToTopOfOrderGiftcardContainer();
	}

	toConfirmationStep(): void {
		/* v8 ignore start */
		if (isPlatformBrowser(this.platformId) && this.sessionStorage) {
			/* v8 ignore stop */
			const orderData = JSON.parse(
				this.sessionStorage.getItem(this.orderDataSessionName)!,
			);
			const orderSummary = JSON.parse(
				this.sessionStorage.getItem(this.orderSummarySessionName)!,
			);

			this.tagManagerFacade.giftcardOrderB2C(
				orderData.amountCards,
				orderSummary.totalIncl / 100,
				orderData.customer.emailAddress,
				orderData.customer.phoneNumber,
			);
		}
		this.orderStep.set(this.orderSteps.Confirmation);
		this.clearFormData();
		this.scrollToTopOfOrderGiftcardContainer();
	}

	startOrderStatusPolling(): void {
		/* v8 ignore start */
		if (isPlatformBrowser(this.platformId)) {
			/* v8 ignore stop */
			this.ngZone.runOutsideAngular(() => {
				this.timeInterval$
					.pipe(
						takeUntil(this.stopPolling$),
						startWith(0),
						filter(() => !!this.orderGUID),
						// NOTE: we use the id to create a unique http GET call due to a bug in hydration, remove the retries when hydration cache is customizable (https://github.com/angular/angular/issues/50117)
						switchMap((id) => {
							return this.giftcardService.getOrderStatus(
								this.orderGUID,
								id,
							);
						}),
						filter((orderStatus) => !!orderStatus),
					)
					.subscribe({
						next: (orderStatus: string) => {
							this.ngZone.run(() => {
								this.handleOrderStatus(orderStatus);
							});
						},
						error: () => {
							this.ngZone.run(() => {
								this.handleOrderStatusError();
							});
						},
					});
			});
		}
	}

	handleOrderStatus(orderStatus: string): void {
		if (
			isPlatformBrowser(this.platformId) &&
			(orderStatus === OrderStatus.success ||
				orderStatus === OrderStatus.pending)
		) {
			this.orderStatus.set(OrderStatus[orderStatus]);
			this.stopPolling$.next();
			this.toConfirmationStep();
		}
	}

	handleOrderStatusError(): void {
		this.alertService.error({
			...alertErrorSettings,
			content: this.translate.instant('global.service.error'),
		});
		this.toSummaryStep();
	}

	scrollToTopOfOrderGiftcardContainer(): void {
		/* v8 ignore start */
		if (isPlatformBrowser(this.platformId)) {
			/* v8 ignore stop */
			const element = this.orderGiftcardContainer.nativeElement;
			const rect = element.getBoundingClientRect();
			if (rect.top < 0 || rect.top > 100) {
				window.scrollTo({
					top: element.offsetTop - 50,
					behavior: 'smooth',
				});
			}
		}
	}

	saveFormData(): void {
		/* v8 ignore start */
		if (isPlatformBrowser(this.platformId) && this.sessionStorage) {
			/* v8 ignore stop */
			this.sessionStorage.setItem(
				this.orderDataSessionName,
				JSON.stringify(this.form.value),
			);

			this.sessionStorage.setItem(
				this.orderSummarySessionName,
				JSON.stringify(this.orderSummary()),
			);
		}
	}

	loadFormData(): void {
		/* v8 ignore start */
		if (isPlatformBrowser(this.platformId) && this.sessionStorage) {
			/* v8 ignore stop */
			const formData = JSON.parse(
				this.sessionStorage.getItem(this.orderDataSessionName)!,
			);
			const orderSummary = JSON.parse(
				this.sessionStorage.getItem(this.orderSummarySessionName)!,
			);
			/* v8 ignore start */
			if (formData && orderSummary) {
				/* v8 ignore stop */
				this.model = formData;
				this.orderSummary.set(orderSummary);
				this.orderStep.set(this.orderSteps.Summary);
				setTimeout(() => {
					this.scrollToTopOfOrderGiftcardContainer();
				}, 250);
			}
		}
	}

	clearFormData(): void {
		/* v8 ignore start */
		if (isPlatformBrowser(this.platformId)) {
			/* v8 ignore stop */
			const { history, location } = window;
			const urlPath = `${location.protocol}//${location.host}${location.pathname}`;
			history.replaceState({}, '', urlPath);

			this.sessionStorage.removeItem(this.orderDataSessionName);
			this.sessionStorage.removeItem(this.orderSummarySessionName);
		}
	}

	scrollToError() {
		setTimeout(() => {
			GeneralHelper.scrollToElementWithClass(
				'form-field-error',
				undefined,
				'input',
			);
		}, 100);
	}

	ngOnDestroy(): void {
		this.stopPolling$.next();
		this.stopPolling$.complete();
		this.stopGUIDcheck$.next();
		this.stopGUIDcheck$.complete();
		this.stopPostalCodeCheck$.next();
		this.stopPostalCodeCheck$.complete();
	}
}
